import type { Session, User } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import type { AdminUser } from '../lib/types';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  staff: AdminUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshStaff: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [staff, setStaff] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStaff = useCallback(async (userId: string | null) => {
    if (!userId) {
      setStaff(null);
      return;
    }

    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', userId)
      .eq('enabled', true)
      .maybeSingle();

    if (error) throw error;
    setStaff((data as AdminUser | null) ?? null);
  }, []);

  const refreshStaff = useCallback(async () => {
    await loadStaff(session?.user.id ?? null);
  }, [loadStaff, session?.user.id]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        await loadStaff(data.session?.user.id ?? null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void initialize();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      window.setTimeout(() => {
        void loadStaff(nextSession?.user.id ?? null).catch(() => setStaff(null));
      }, 0);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [loadStaff]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await loadStaff(data.user.id);
  }, [loadStaff]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setStaff(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      staff,
      loading,
      signIn,
      signOut,
      refreshStaff,
    }),
    [loading, refreshStaff, session, signIn, signOut, staff],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth doit être utilisé dans AuthProvider.');
  return value;
}
